'use client';

import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import {useState, ReactNode} from 'react'
import { motion } from 'framer-motion'

interface ListProps {
    title: string | null;
    icon: ReactNode | null;
    options: Options[];
    value?: string; // Controlled component için
    onChange?: (value: string) => void; // Controlled component için
}
interface Options {
    title: string;
    value: string;
    id: number;
    icon: ReactNode | null;
}
export default function ListBox({title, icon, options, value, onChange}: ListProps) {
    // Default option fallback
    const defaultOption: Options = { title: 'Select...', value: '', id: 0, icon: null }
    
    // Controlled component: value prop'u varsa onu kullan, yoksa internal state
    const isControlled = value !== undefined && onChange !== undefined;
    const [internalSelected, setInternalSelected] = useState(options[0] || defaultOption)
    
    // Controlled component için selected değerini bul
    const selected = isControlled 
        ? (options.find(opt => opt.value === value) || options[0] || defaultOption)
        : (internalSelected || defaultOption)
    
    // Eğer selected hala undefined ise, defaultOption kullan
    const safeSelected = selected || defaultOption
    
    const handleChange = (newOption: Options) => {
        if (isControlled) {
            onChange?.(newOption.value)
        } else {
            setInternalSelected(newOption)
        }
    }

    return (
        <motion.div className="w-full relative z-100" variants={{hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 }}}>
            {title && (<h3 className="text-alsero-dark-gray text-lg mb-1">{title}</h3>)}
            <Listbox value={safeSelected} onChange={handleChange} by="value">
                {({ open }) => (
                    <>
                        <ListboxButton
                            className={clsx(
                                'relative cursor-pointer flex flex-row justify-between items-center px-6 py-3 bg-white border border-gray-300 rounded-lg w-full min-h-[60px]',
                                'focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-white/25'
                            )}
                        >
                            <div className="flex flex-row gap-2 items-center">
                                {icon && (<>{icon}</>)}
                                <span className="text-alsero-dark-gray text-lg">{safeSelected.title}</span>
                            </div>
                            <svg width="16" height="8" viewBox="0 0 16 8" fill="none" xmlns="http://www.w3.org/2000/svg" 
                            className={clsx('transition-transform duration-200 fill-alsero-dark-gray', open && 'rotate-180')}>
                                <path fillRule="evenodd" clipRule="evenodd" d="M14.7674 0.454837C15.1264 0.7968 15.1264 1.35123 14.7674 1.6932L8.63965 7.53088C8.2807 7.87284 7.69872 7.87284 7.33976 7.53088L1.21201 1.6932C0.853056 1.35123 0.853056 0.7968 1.21201 0.454836C1.57097 0.112873 2.15295 0.112873 2.5119 0.454836C5.57922 3.37696 10.4002 3.37696 13.4675 0.454837C13.8265 0.112873 14.4084 0.112873 14.7674 0.454837Z"/>
                            </svg>
                        </ListboxButton>
                        <ListboxOptions
                            anchor="bottom"
                            className={clsx(
                                'w-(--button-width) relative z-100 rounded-xl bg-white p-1 shadow-lg [--anchor-gap:--spacing(1)] focus:outline-none'
                            )}
                            as={motion.div}
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={{
                                open: {
                                    opacity: 1,
                                    scale: 1,
                                    y: 0,
                                    transition: {
                                        type: "spring",
                                        stiffness: 300,
                                        damping: 24,
                                        staggerChildren: 0.05,
                                        delayChildren: 0.1
                                    }
                                },
                                closed: {
                                    opacity: 0,
                                    scale: 0.95,
                                    y: -10,
                                    transition: {
                                        type: "spring",
                                        stiffness: 300,
                                        damping: 24
                                    }
                                }
                            }}
                        >
                            {options.length === 0 ? (
                                <div className="px-3 py-1.5 text-sm text-alsero-light-gray">
                                    No options available
                                </div>
                            ) : (
                                options.map((option) => (
                                    <ListboxOption
                                        key={option.value+'_'+option.id}
                                        value={option}
                                    className="group flex cursor-pointer items-center gap-2 rounded-xl px-3 py-1.5 select-none data-focus:bg-alsero-soft-white/30"
                                    as={motion.div}
                                    variants={{
                                        open: {
                                            opacity: 1,
                                            x: 0,
                                            transition: {
                                                type: "spring",
                                                stiffness: 300,
                                                damping: 24
                                            }
                                        },
                                        closed: {
                                            opacity: 0,
                                            x: -10
                                        }
                                    }}
                                > 
                                        <CheckIcon className="invisible size-4 fill-alsero-dark-gray group-data-selected:visible" />
                                        <div className="text-lg text-alsero-dark-gray font-sans">{option.title}</div>
                                    </ListboxOption>
                                ))
                            )}
                        </ListboxOptions>
                    </>
                )}
            </Listbox>
        </motion.div>
    )
}
